import React from 'react';
import { chakra } from '@chakra-ui/react';
import { Point2 } from '../../../../domain/geometry/geometric-types';
import { kilerMap } from '../../ProductChoices';
import { Diamond, KileTypeSKU } from './wedge-types';

interface WedgePreviewLabelProps {
  position: Point2;
  kileType: KileTypeSKU;
  diamond: Diamond;
  getPointInDomCoordinateSystem: (point: Point2) => Point2;
  unitScale: number | null;
  tangentVector?: Point2; // Direction vector of the wedge
}

// Helper component to render a single text label with rotation
const LabelText: React.FC<{
  x: number;
  y: number;
  text: string;
  rotation: number; // Rotation angle in degrees
}> = ({ x, y, text, rotation }) => (
  <text
    x={x}
    y={y}
    textAnchor="middle"
    fill="black"
    fontWeight="bold"
    fontSize="12px"
    dominantBaseline="middle"
    transform={`rotate(${rotation}, ${x}, ${y})`}
  >
    {text}
  </text>
);

// Helper function to check if should show labels
const shouldShowLabels = (productLength: number, diamond: Diamond): boolean => {
  return (
    productLength > 0 && !Object.values(diamond).every((triangle) => !triangle)
  );
};

// Helper function to check if should show two labels
const shouldShowTwoLabels = (diamond: Diamond): boolean => {
  const hasFrontTriangle = diamond.triangle1 || diamond.triangle3;
  const hasBackTriangle = diamond.triangle2 || diamond.triangle4;
  return hasFrontTriangle && hasBackTriangle;
};

// Normalize a vector to unit length
const normalizeVector = (vector: Point2): Point2 => {
  const length = Math.sqrt(vector[0] * vector[0] + vector[1] * vector[1]);
  if (length === 0) return [1, 0]; // Default to x-axis if vector is zero
  return [vector[0] / length, vector[1] / length];
};

// Calculate the angle in degrees from a vector, ensuring text is never upside down
const getReadableAngle = (vector: Point2): number => {
  // Get the base angle in degrees (atan2 returns angle in radians)
  const angleRad = Math.atan2(vector[1], vector[0]);
  let angleDeg = angleRad * (180 / Math.PI);

  // Normalize the angle to -180 to 180 range
  if (angleDeg > 180) angleDeg -= 360;
  if (angleDeg < -180) angleDeg += 360;

  // Ensure text is right-side up
  // For any angle that would result in upside-down text, flip it 180 degrees
  if (angleDeg > 90 || angleDeg < -90) {
    angleDeg += 180;
    if (angleDeg > 180) angleDeg -= 360; // Keep in the -180 to 180 range
  }

  // Negate the angle for SVG rotation
  return -angleDeg;
};

export const WedgePreviewLabel: React.FC<WedgePreviewLabelProps> = ({
  position,
  kileType,
  diamond,
  getPointInDomCoordinateSystem,
  unitScale,
  tangentVector = [1, 0], // Default to x-axis if not provided
}) => {
  // Guard against missing props
  if (!kileType || !diamond) {
    console.warn('Missing required props in WedgePreviewLabel');
    return null;
  }

  // Get product length from kilerMap
  const product = kilerMap[kileType]?.[0];
  const productLength = product?.length || 0;
  const segmentLength = productLength * 2;

  // Log if kileType doesn't map to a product
  if (!product) {
    console.warn(`No product found for kileType: ${kileType}`);
  }

  // Don't render if unitScale is not available
  if (!unitScale) {
    return null;
  }

  // Don't render if there's no valid length or all triangles are disabled
  if (!shouldShowLabels(segmentLength, diamond)) {
    return null;
  }

  const showTwoLabels = shouldShowTwoLabels(diamond);
  const labelText = `${productLength.toFixed(1)}m`;

  // Calculate offset distance
  const offsetDistance = productLength / 4 / unitScale;

  // Normalize the tangent vector
  const normalizedTangent = normalizeVector(tangentVector);

  // Calculate text rotation angle
  const textAngle = getReadableAngle(normalizedTangent);

  if (showTwoLabels) {
    // Calculate offset based on the tangent direction
    // Multiply the normalized tangent by the offset distance
    const frontPosition: Point2 = [
      position[0] - normalizedTangent[0] * offsetDistance,
      position[1] - normalizedTangent[1] * offsetDistance,
    ];

    const backPosition: Point2 = [
      position[0] + normalizedTangent[0] * offsetDistance,
      position[1] + normalizedTangent[1] * offsetDistance,
    ];

    // Convert both to DOM coordinates
    const domFrontPosition = getPointInDomCoordinateSystem(frontPosition);
    const domBackPosition = getPointInDomCoordinateSystem(backPosition);

    return (
      <chakra.g pointerEvents="none">
        <LabelText
          x={domFrontPosition[0]}
          y={domFrontPosition[1]}
          text={labelText}
          rotation={textAngle}
        />
        <LabelText
          x={domBackPosition[0]}
          y={domBackPosition[1]}
          text={labelText}
          rotation={textAngle}
        />
      </chakra.g>
    );
  } else {
    // Single label
    const domPosition = getPointInDomCoordinateSystem(position);
    return (
      <chakra.g pointerEvents="none">
        <LabelText
          x={domPosition[0]}
          y={domPosition[1]}
          text={labelText}
          rotation={textAngle}
        />
      </chakra.g>
    );
  }
};
